import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"
import { createLLMOMeta, createBreadcrumbSchema, createToolSchema, createHowToSchema, createFAQSchema } from "@/lib/seo"
import { getToolBySlug } from "@/data/tools/tools-registry"
import ToolPageLayout from "@/components/tools/ToolPageLayout"
import { SelectField, SubmitButton, ResetButton } from "@/components/tools/ToolFormField"
import { SectionHeading, Paragraph, FeatureGrid, TierCards, TipList, ContentSection } from "@/components/tools/SeoContent"

const tool = getToolBySlug("instagram-caption-generator")!

export const Route = createFileRoute("/tools/instagram-caption-generator")({
  head: () => ({
    meta: [
      ...createLLMOMeta({
        title: tool.seo.title,
        description: tool.seo.description,
        url: "https://zapify.pro/tools/instagram-caption-generator",
      }),
      { name: "keywords", content: tool.seo.keywords.join(", ") },
    ],
    links: [{ rel: "canonical", href: "https://zapify.pro/tools/instagram-caption-generator" }],
    scripts: [
      createToolSchema({ name: tool.name, description: tool.seo.description, url: "https://zapify.pro/tools/instagram-caption-generator" }),
      createBreadcrumbSchema({ itemListElement: [
        { position: 1, name: "Home", item: "https://zapify.pro" },
        { position: 2, name: "Free Tools", item: "https://zapify.pro/tools" },
        { position: 3, name: tool.shortName, item: "https://zapify.pro/tools/instagram-caption-generator" },
      ]}),
      createHowToSchema({ name: "How to Generate Instagram Captions", description: tool.seo.description, steps: tool.howToSteps }),
      createFAQSchema(tool.faq),
    ],
  }),
  component: CaptionGeneratorPage,
})

// ---------------------------------------------------------------------------
// Template engine
// ---------------------------------------------------------------------------

const templates: Record<string, string[]> = {
  casual: [
    "Just discovered the best way to {topic} and I'm obsessed! Who else is into this?",
    "POV: You finally figured out {topic}. Game. Changed.",
    "Real talk — {topic} hits different when you actually commit to it.",
    "That feeling when {topic} just clicks. If you know, you know.",
    "Not gonna lie, {topic} has been my whole vibe lately. Anyone else?",
    "Can we talk about {topic} for a sec? Because WOW.",
    "Started {topic} and honestly? Best decision ever. Here's why...",
    "Hot take: {topic} is underrated and more people need to try it.",
    "Okay but why did nobody tell me about {topic} sooner? Life changed.",
    "Currently obsessing over {topic}. No regrets whatsoever.",
  ],
  professional: [
    "Here's what I've learned about {topic} after months of research and real-world testing.",
    "The key to {topic} isn't what most people think. Let me break it down.",
    "I've tested dozens of approaches to {topic}. These are the results that actually matter.",
    "{topic} is evolving fast. Here's what the data tells us about where it's heading.",
    "Three principles I follow for {topic} that consistently deliver measurable results.",
    "The biggest misconception about {topic}? That it has to be complicated. Here's the framework I use.",
    "After working with {topic} for years, this is the #1 strategy that moves the needle.",
    "Let's talk about {topic}. The insights from this quarter are worth sharing.",
    "If you're serious about {topic}, here's the approach that separates amateurs from professionals.",
    "{topic} deserves more strategic attention than most people give it. Here's my take.",
  ],
  funny: [
    "Me trying to explain {topic} to my friends: *cue blank stares*",
    "My therapist: '{topic} can't control your life.' Me: *looks at screen time*",
    "Nobody: ... Absolutely nobody: ... Me at 2am: researching {topic}",
    "Plot twist: {topic} was the answer all along. Who knew?",
    "Telling myself I'll spend 5 minutes on {topic}... 3 hours later...",
    "If {topic} was a person, I'd marry it. No questions asked.",
    "Roses are red, violets are blue, {topic} is amazing, and that's the truth.",
    "My bank account after I discovered {topic}: *nervous laughter*",
    "Relationship status: in a committed relationship with {topic}.",
    "Warning: {topic} may cause extreme happiness, productivity, and an urge to tell everyone you know.",
  ],
  inspirational: [
    "Your journey with {topic} starts with a single step. Don't wait for perfect — start now.",
    "Remember: every expert in {topic} was once a beginner. Keep going.",
    "The magic happens when you stop overthinking {topic} and start doing.",
    "{topic} taught me that growth isn't always comfortable, but it's always worth it.",
    "You don't need permission to pursue {topic}. You just need to begin.",
    "One year from now, you'll wish you started {topic} today. This is your sign.",
    "Success with {topic} isn't about talent. It's about showing up when it's hard.",
    "The world needs what you bring to {topic}. Don't dim your light.",
    "What if I told you that {topic} could change everything? Because it can — and it will.",
    "Stop waiting for the right moment to start {topic}. The right moment is right now.",
  ],
  educational: [
    "Here's what most people get wrong about {topic} — and how to fix it.",
    "Did you know? {topic} has more impact than you think. Here are the facts.",
    "Breaking down {topic}: the complete guide you didn't know you needed.",
    "3 things I wish someone told me about {topic} before I started.",
    "The science behind {topic} is fascinating. Let me share what I've learned.",
    "{topic} 101: Save this post for the next time you need a refresher.",
    "Common myths about {topic}, debunked with real data and experience.",
    "A quick thread on {topic} that could save you hours of trial and error.",
    "If you want to understand {topic}, start with these fundamentals.",
    "I studied {topic} so you don't have to. Here's the cheat sheet.",
  ],
  promotional: [
    "Introducing something special for anyone serious about {topic}. You're going to love this.",
    "We built this for the {topic} community — and the response has been incredible.",
    "Struggling with {topic}? We created the solution you've been asking for.",
    "Big announcement: everything you need for {topic}, all in one place.",
    "This is for everyone who's told us they need help with {topic}. We listened.",
    "Ready to level up your {topic} game? Here's exactly what you need.",
    "Thousands of people are already crushing it with {topic} using this. Are you next?",
    "Limited time: get the ultimate {topic} toolkit and start seeing results today.",
    "If {topic} is on your radar, you'll want to see what we've been working on.",
    "Your {topic} strategy is about to get a serious upgrade. Here's how.",
  ],
}

const ctaOptions = [
  "Save this for later!",
  "Drop a comment if you agree!",
  "Tag someone who needs to see this!",
  "Link in bio for more!",
  "What do you think? Let me know below!",
  "Share this with a friend who needs it!",
  "Double tap if this resonates!",
  "Follow for more content like this!",
]

const emojiSets = [
  ["✨", "💫", "🌟"],
  ["🔥", "💥", "⚡"],
  ["💪", "🙌", "🎯"],
  ["❤️", "💛", "🧡"],
  ["🚀", "📈", "💡"],
  ["😍", "🤩", "💯"],
  ["🎉", "🥳", "✅"],
  ["👀", "👇", "💬"],
]

function generateHashtags(topic: string): string {
  const base = ["#instagram", "#instagood", "#viral"]
  const topicTag = "#" + topic.toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 20)
  const extra = ["#growth", "#contentcreator", "#socialmedia", "#trending"]
  return [...base, topicTag, ...extra.slice(0, 2)].join(" ")
}

function pickRandom<T>(arr: T[], count: number): T[] {
  const shuffled = [...arr].sort(() => Math.random() - 0.5)
  return shuffled.slice(0, count)
}

type LengthOption = "short" | "medium" | "long"

function expandCaption(template: string, topic: string, length: LengthOption): string {
  let caption = template.replace(/\{topic\}/g, topic)

  if (length === "medium") {
    const extensions = [
      ` It's been a game-changer for my routine.`,
      ` Honestly, the results speak for themselves.`,
      ` And the best part? Anyone can get started.`,
      ` I've seen a real difference since I committed to it.`,
      ` The more I learn, the more I realize how powerful this is.`,
    ]
    caption += pickRandom(extensions, 1)[0]
  } else if (length === "long") {
    const extensions = [
      ` I've been diving deep into this for a while now, and the journey has been incredible. Every step has taught me something new.`,
      ` What started as curiosity turned into a full passion. The results keep getting better the more consistent I am.`,
      ` I want to share everything I've learned because it's made such a difference. Stay tuned for more on this topic.`,
      ` The transformation has been real. If you're on the fence, this is your sign to go all in and see what happens.`,
      ` From where I started to where I am now — the growth has been unreal. Sharing this so you can experience it too.`,
    ]
    caption += pickRandom(extensions, 1)[0]
  }

  return caption
}

function addEmojis(caption: string): string {
  const set = pickRandom(emojiSets, 1)[0]
  const emoji = pickRandom(set, 2)
  return `${emoji[0]} ${caption} ${emoji[1]}`
}

type GeneratedCaption = {
  text: string
  id: number
}

function generateCaptions(
  topic: string,
  tone: string,
  length: LengthOption,
  includeHashtags: boolean,
  includeEmojis: boolean,
  includeCta: boolean,
): GeneratedCaption[] {
  const toneKey = tone.toLowerCase()
  const toneTemplates = templates[toneKey] || templates.casual
  const selected = pickRandom(toneTemplates, 3)

  return selected.map((template, i) => {
    let caption = expandCaption(template, topic, length)

    if (includeEmojis) {
      caption = addEmojis(caption)
    }

    if (includeCta) {
      const cta = pickRandom(ctaOptions, 1)[0]
      caption += `\n\n${cta}`
    }

    if (includeHashtags) {
      const hashtags = generateHashtags(topic)
      caption += `\n\n${hashtags}`
    }

    return { text: caption, id: Date.now() + i }
  })
}

// ---------------------------------------------------------------------------
// Tone & length options
// ---------------------------------------------------------------------------

const toneOptions = [
  { value: "casual", label: "Casual & Friendly" },
  { value: "professional", label: "Professional" },
  { value: "funny", label: "Funny & Playful" },
  { value: "inspirational", label: "Inspirational" },
  { value: "educational", label: "Educational" },
  { value: "promotional", label: "Promotional" },
]

const lengthOptions = [
  { value: "short", label: "Short (1-2 sentences)" },
  { value: "medium", label: "Medium (2-3 sentences)" },
  { value: "long", label: "Long (4-5 sentences)" },
]

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function CaptionGeneratorPage() {
  const [topic, setTopic] = useState("")
  const [tone, setTone] = useState("casual")
  const [length, setLength] = useState<LengthOption>("medium")
  const [includeHashtags, setIncludeHashtags] = useState(true)
  const [includeEmojis, setIncludeEmojis] = useState(true)
  const [includeCta, setIncludeCta] = useState(false)

  const [results, setResults] = useState<GeneratedCaption[]>([])
  const [copiedId, setCopiedId] = useState<number | null>(null)
  const [hasGenerated, setHasGenerated] = useState(false)

  const handleGenerate = () => {
    if (!topic.trim()) return
    const captions = generateCaptions(topic.trim(), tone, length, includeHashtags, includeEmojis, includeCta)
    setResults(captions)
    setCopiedId(null)
    setHasGenerated(true)
  }

  const handleReset = () => {
    setTopic("")
    setTone("casual")
    setLength("medium")
    setIncludeHashtags(true)
    setIncludeEmojis(true)
    setIncludeCta(false)
    setResults([])
    setCopiedId(null)
    setHasGenerated(false)
  }

  const copyToClipboard = async (text: string, id: number) => {
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      const textarea = document.createElement("textarea")
      textarea.value = text
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand("copy")
      document.body.removeChild(textarea)
    }
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  const isFormView = !hasGenerated || results.length === 0

  return (
    <ToolPageLayout
      tool={tool}
      content={
        <div className="space-y-10">
          <ContentSection>
            <SectionHeading>Why Great Captions Matter on Instagram</SectionHeading>
            <Paragraph>
              Your image or video stops the scroll, but your caption is what converts that pause into real engagement. Instagram captions are the second hook — the element that turns a passive viewer into someone who comments, saves, shares, or follows. In an era where the algorithm prioritizes meaningful interactions over vanity metrics, the caption has become one of the most powerful levers creators and brands have to influence how far their content travels.
            </Paragraph>
            <Paragraph>
              Instagram's ranking algorithm weighs caption-driven engagement heavily. When someone leaves a comment, saves a post for later, or shares it to a friend via DM, each of those signals tells the algorithm that this content is worth showing to more people. Captions that ask questions, spark debate, or offer genuine value naturally generate these high-value interactions. Posts with captions over 100 words consistently earn more saves and shares than their shorter counterparts, because longer captions increase the time a user spends on your post — a metric Instagram tracks closely to gauge content quality.
            </Paragraph>
            <Paragraph>
              Time-on-post is one of the quieter but most impactful ranking factors. When a user stops scrolling and spends 10, 20, or 30 seconds reading your caption, Instagram interprets that dwell time as a strong quality signal. This is why educational carousels and storytelling posts with detailed captions frequently outperform quick, throwaway text. The longer someone stays engaged with your content, the more likely the algorithm is to push it into Explore, recommend it in suggested posts, and surface it at the top of followers' feeds.
            </Paragraph>
            <Paragraph>
              Beyond algorithmic benefits, great captions build community. They give your audience a reason to interact with you rather than simply double-tap and move on. A well-written caption can make someone feel seen, teach them something new, or make them laugh — and those emotional responses are what turn casual followers into loyal fans. Over time, consistent caption quality creates a feedback loop: more engagement leads to more reach, which leads to more followers, which leads to even more engagement. If you are serious about growing on Instagram in 2026, your caption strategy deserves just as much attention as your content calendar and visual aesthetic.
            </Paragraph>
          </ContentSection>
          <ContentSection>
            <SectionHeading>Anatomy of a High-Performing Caption</SectionHeading>
            <FeatureGrid columns={3} items={[
              { icon: "\uD83C\uDFA3", title: "The Hook (First Line)", description: "Only the first 125 characters show in the feed before Instagram truncates your caption with a \"more\" link. Your opening line is the gatekeeper — it determines whether someone taps to expand or keeps scrolling. The best hooks create a curiosity gap, make a bold claim, or speak directly to a pain point your audience feels. Think of it like a headline: \"I wasted $10K on ads before discovering this\" is far more compelling than \"Here are some marketing tips.\" Front-load the most interesting or surprising part of your message, and never waste the first line on filler words or greetings." },
              { icon: "\uD83D\uDCDD", title: "The Body (Value or Story)", description: "The body of your caption is where you deliver on the promise your hook made. This is your chance to share a personal story, teach something actionable, or make your audience feel a genuine emotion. The most effective caption bodies use short paragraphs, line breaks for readability, and a conversational tone that makes the reader feel like you are speaking directly to them. Avoid dense walls of text — break your message into digestible chunks that are easy to scan on a mobile screen. Every sentence should either add value or move the narrative forward." },
              { icon: "\uD83C\uDFAF", title: "The Call-to-Action (CTA)", description: "Never assume your audience knows what to do next. Tell them explicitly: \"Drop a comment with your biggest challenge,\" \"Save this for your next launch,\" or \"Share this with someone who needs to hear it.\" Posts with a clear, specific CTA generate significantly more comments and saves than those that end passively. The key is to make your CTA feel natural and connected to the content — not a tacked-on afterthought. Ask a question that genuinely invites a response, or suggest an action that adds value for the reader, like saving a tip they will need later." },
            ]} />
          </ContentSection>
          <ContentSection>
            <SectionHeading>Caption Writing Tips by Tone</SectionHeading>
            <FeatureGrid columns={3} items={[
              { icon: "\uD83D\uDE0E", title: "Casual", description: "Write like you are texting a close friend. Use \"you\" frequently, lean into contractions, and do not be afraid of sentence fragments or slang. Casual captions feel authentic because they strip away the corporate polish and speak at eye level. This tone works exceptionally well for lifestyle content, behind-the-scenes posts, and personal updates where your audience wants to connect with the real person behind the account." },
              { icon: "\uD83D\uDCBC", title: "Professional", description: "Lead with data, research, or first-hand experience to establish credibility. Professional captions should be polished and well-structured but never stiff or overly formal — your audience is on Instagram, not reading a white paper. Avoid slang and excessive emojis, but do use conversational transitions to keep the text flowing. This tone is ideal for B2B content, thought leadership, case studies, and industry insights where authority matters." },
              { icon: "\uD83D\uDE02", title: "Funny", description: "Self-deprecating humor, pop culture references, and unexpected punchlines are your best tools here. The key is to keep it genuine — forced humor falls flat and can actually hurt engagement. Funny captions work best when they tap into shared experiences your audience relates to, like the universal struggle of waking up early or the chaos of running a small business. If the joke does not make you laugh when you read it back, try a different angle." },
              { icon: "\u2728", title: "Inspirational", description: "The best inspirational captions pair vulnerability with hope. Share a real struggle you faced and the lesson it taught you — not a generic motivational quote copied from a poster. Personal stories of failure, growth, and transformation resonate far more than abstract platitudes. Use short, punchy sentences for emotional impact, and close with a forward-looking statement that empowers your reader to take action in their own life." },
              { icon: "\uD83D\uDCDA", title: "Educational", description: "Numbered tips, step-by-step breakdowns, and myth-busting formats perform exceptionally well because they promise — and deliver — clear, actionable value. Structure your caption so each point is concise and immediately useful. Educational captions are the most saved content type on Instagram, which makes them algorithm gold. Always end with a takeaway the reader can implement today, not just theoretical knowledge." },
              { icon: "\uD83D\uDCE3", title: "Promotional", description: "The biggest mistake in promotional captions is leading with the product or feature instead of the problem it solves. Start by describing a pain point your audience experiences, then naturally introduce your solution as the answer. Create urgency through limited-time offers or social proof (\"2,000 creators already use this\") rather than aggressive sales language. Always include a soft but clear CTA — \"Link in bio\" or \"DM us to get started\" — so the path to conversion is obvious." },
            ]} />
          </ContentSection>
          <ContentSection>
            <SectionHeading>When to Use Short vs. Long Captions</SectionHeading>
            <TierCards tiers={[
              { label: "Short", range: "1-2 sentences", description: "Best suited for Reels, aesthetic photo posts, and quote graphics where the visual carries the message. When the image or video speaks for itself, a brief caption acts as a complement rather than a distraction. Short captions also work well for high-frequency posting schedules where you want to maintain presence without demanding too much from your audience. Think punchy one-liners, rhetorical questions, or simple emotional reactions that invite a quick double-tap.", color: "green" },
              { label: "Medium", range: "2-3 sentences", description: "The most versatile length and work for the majority of daily feed posts. They give you enough space to add context, share a quick insight, or set up a CTA without overwhelming the reader. Medium captions hit the sweet spot of providing value while respecting the fast-scrolling nature of the Instagram feed. They are particularly effective for product posts, event announcements, and lifestyle content where a bit of story enhances the visual.", color: "blue" },
              { label: "Long", range: "4-5 sentences or more", description: "Ideal for storytelling, educational breakdowns, and posts designed to build deep connections with your audience. Long-form captions increase dwell time, which boosts algorithmic ranking, and they tend to generate more saves because readers bookmark them for future reference. Use long captions when you have a genuine story to tell, a detailed tip to share, or a perspective that requires nuance. The key is to make every sentence count — long does not mean padded. If your audience is engaged, they will read every word.", color: "yellow" },
            ]} />
          </ContentSection>
          <ContentSection>
            <SectionHeading>Hashtag Strategy for Captions</SectionHeading>
            <Paragraph>
              Hashtags remain a valuable discovery tool on Instagram, but the strategy around them has evolved significantly. The debate between placing hashtags at the end of your caption versus in the first comment continues, but both approaches work — what matters more is relevance and intent. Placing them in the caption keeps everything in one place and is slightly easier for the algorithm to index immediately. Placing them in the first comment keeps your caption visually clean, which some creators prefer for aesthetic reasons. Test both and see which approach your audience responds to.
            </Paragraph>
            <TipList tips={[
              { title: "Use 3-5 highly relevant hashtags", description: "In 2026, the consensus among top creators and Instagram's own guidance is to use 3 to 5 highly relevant hashtags rather than stuffing 30 generic ones. Quality beats quantity. Each hashtag should accurately describe the content of your post so that the people who discover it through that hashtag are genuinely interested in what you are sharing." },
              { title: "Mix niche and moderately popular tags", description: "Mix niche hashtags (under 100K posts) with moderately popular ones (100K to 1M posts) to balance discoverability with competition. Avoid banned or flagged hashtags, which can silently suppress your reach — Instagram periodically restricts hashtags associated with spam or guideline violations." },
              { title: "Prioritize relevance over popularity", description: "A hashtag with 500 million posts might seem attractive, but your content will be buried within seconds. A niche hashtag with 50,000 posts, on the other hand, puts you in front of a targeted audience that is far more likely to engage. Think of hashtags as search keywords: choose the ones your ideal follower would actually search for, not the ones with the highest post count." },
            ]} />
          </ContentSection>
          <ContentSection>
            <SectionHeading>From Caption to Conversation</SectionHeading>
            <Paragraph>
              A great caption does not just earn a like — it starts a conversation. When your caption asks a compelling question, shares a polarizing opinion, or offers genuine value, your audience responds in the comments. And comments are where the real magic happens: every comment is an opportunity to build a relationship, answer a question, or guide someone toward the next step in their journey with your brand.
            </Paragraph>
            <Paragraph>
              This is where DM automation tools like Zapify transform comment engagement into measurable business results. Imagine posting a carousel with the caption "Comment GUIDE and I'll DM you the full breakdown." Every person who comments that keyword instantly receives a personalized direct message with your lead magnet, product link, or detailed resource. You have turned a passive comment into an active conversation — and a warm lead — without lifting a finger after hitting publish.
            </Paragraph>
            <Paragraph>
              The caption-to-DM pipeline is one of the most effective growth strategies on Instagram today. Your caption generates engagement (boosting algorithmic reach), the comments signal interest (building social proof), and the automated DM delivers value directly to the person's inbox (creating a private, high-conversion touchpoint). By combining thoughtful caption writing with smart automation, you close the loop between content creation and lead generation — making every post you publish work harder for your business.
            </Paragraph>
          </ContentSection>
        </div>
      }
    >
      <div className="max-w-2xl mx-auto">
        {isFormView ? (
          /* ---------------------------------------------------------------- */
          /* Form Card                                                        */
          /* ---------------------------------------------------------------- */
          <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-6 sm:p-8 shadow-sm space-y-5">
            {/* Topic */}
            <div>
              <label className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-1.5">
                What's your post about? <span className="text-red-500 ml-0.5">*</span>
              </label>
              <textarea
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="e.g. morning workout routine, new product launch, travel in Bali..."
                rows={3}
                className="w-full rounded-xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50 px-4 py-3 text-sm text-neutral-900 dark:text-white placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-brand-accent/40 focus:border-brand-accent transition-all resize-none"
              />
            </div>

            {/* Tone & Length */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <SelectField
                label="Tone"
                value={tone}
                onChange={setTone}
                options={toneOptions}
                required
              />
              <SelectField
                label="Length"
                value={length}
                onChange={(v) => setLength(v as LengthOption)}
                options={lengthOptions}
                required
              />
            </div>

            {/* Checkboxes */}
            <div className="flex flex-wrap gap-x-6 gap-y-3">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={includeHashtags}
                  onChange={(e) => setIncludeHashtags(e.target.checked)}
                  className="h-4 w-4 rounded border-neutral-300 dark:border-neutral-600 text-brand-accent focus:ring-brand-accent/40 accent-brand-accent"
                />
                <span className="text-sm text-neutral-700 dark:text-neutral-300">Include Hashtags</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={includeEmojis}
                  onChange={(e) => setIncludeEmojis(e.target.checked)}
                  className="h-4 w-4 rounded border-neutral-300 dark:border-neutral-600 text-brand-accent focus:ring-brand-accent/40 accent-brand-accent"
                />
                <span className="text-sm text-neutral-700 dark:text-neutral-300">Include Emojis</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={includeCta}
                  onChange={(e) => setIncludeCta(e.target.checked)}
                  className="h-4 w-4 rounded border-neutral-300 dark:border-neutral-600 text-brand-accent focus:ring-brand-accent/40 accent-brand-accent"
                />
                <span className="text-sm text-neutral-700 dark:text-neutral-300">Include CTA</span>
              </label>
            </div>

            {/* Generate Button */}
            <SubmitButton onClick={handleGenerate} disabled={!topic.trim()}>
              Generate Captions
            </SubmitButton>
          </div>
        ) : (
          /* ---------------------------------------------------------------- */
          /* Results View                                                     */
          /* ---------------------------------------------------------------- */
          <div className="space-y-4">
            {/* Caption cards */}
            {results.map((caption, index) => (
              <div
                key={caption.id}
                className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-5 sm:p-6 shadow-sm"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-semibold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider">
                    Variation {index + 1}
                  </span>
                  <button
                    onClick={() => copyToClipboard(caption.text, caption.id)}
                    className={`h-8 px-4 rounded-lg text-xs font-semibold transition-all duration-300 ${
                      copiedId === caption.id
                        ? "bg-green-500 text-white"
                        : "bg-brand-accent text-white shadow-[0_2px_8px_rgba(244,63,94,0.15)] hover:shadow-[0_4px_14px_rgba(244,63,94,0.15)] hover:-translate-y-0.5"
                    }`}
                  >
                    {copiedId === caption.id ? "Copied!" : "Copy"}
                  </button>
                </div>
                <p className="text-sm sm:text-base text-neutral-800 dark:text-neutral-200 leading-relaxed whitespace-pre-line">
                  {caption.text}
                </p>
              </div>
            ))}

            {/* Action buttons */}
            <div className="grid grid-cols-2 gap-3 pt-2">
              <SubmitButton onClick={handleGenerate} disabled={false}>
                Generate More
              </SubmitButton>
              <ResetButton onClick={handleReset}>
                Edit Inputs
              </ResetButton>
            </div>
          </div>
        )}

        {/* Empty state hint */}
        {!hasGenerated && !topic.trim() && (
          <div className="mt-6 rounded-2xl border border-dashed border-neutral-300 dark:border-neutral-700 p-8 text-center">
            <p className="text-sm text-neutral-400 dark:text-neutral-500">
              Describe your post topic and choose a style to generate 3 unique captions
            </p>
          </div>
        )}
      </div>
    </ToolPageLayout>
  )
}
